import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider, useMediaQuery } from '@mui/material';
import { makeTheme } from '../theme';

/** What the person chose: a fixed mode, or follow the computer's setting. */
export type ColorPreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'shoutvector.colorMode';

/**
 * Light / dark / match the computer, remembered per browser.
 *
 * localStorage is fine here: it is a per-viewer convenience, and if it is unavailable (private
 * window, blocked storage) the app simply starts light — nothing breaks.
 *
 * Light is the default rather than "match the computer": dark mode arrived 2026-10-17, and anyone
 * whose computer is set to dark would otherwise have found the app changed without asking.
 */
const readPreference = (): ColorPreference => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    return 'light';
  }
};

const ColorModeContext = createContext<{
  preference: ColorPreference;
  mode: 'light' | 'dark';
  setPreference: (p: ColorPreference) => void;
}>({ preference: 'light', mode: 'light', setPreference: () => {} });

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const [preference, setPref] = useState<ColorPreference>(readPreference);
  const mode: 'light' | 'dark' = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  const setPreference = (p: ColorPreference) => {
    setPref(p);
    try { window.localStorage.setItem(STORAGE_KEY, p); } catch { /* a convenience, not required */ }
  };

  const theme = useMemo(() => makeTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={{ preference, mode, setPreference }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};
